import { Monitor, Moon, Sun } from "lucide-react";

import { useTheme, type ThemeMode } from "@/lib/theme/ThemeProvider";
import { cn } from "@/lib/utils";

/**
 * Theme toggle. Three icon-buttons (system / light / dark) shown in the
 * header. Compact on purpose — matches OpenClaw's restrained chrome.
 *
 * Keyboard accessible: each button is a real `<button>` with an
 * `aria-pressed` reflecting current state.
 */
export function ThemeToggle() {
  const { mode, setMode } = useTheme();
  return (
    <div
      className="inline-flex items-center rounded-md border border-border/60 bg-card p-0.5"
      role="group"
      aria-label="Theme"
    >
      <ToggleButton
        active={mode === "system"}
        label="System theme"
        onClick={() => setMode("system")}
      >
        <Monitor className="h-4 w-4" aria-hidden="true" />
      </ToggleButton>
      <ToggleButton
        active={mode === "light"}
        label="Light theme"
        onClick={() => setMode("light")}
      >
        <Sun className="h-4 w-4" aria-hidden="true" />
      </ToggleButton>
      <ToggleButton
        active={mode === "dark"}
        label="Dark theme"
        onClick={() => setMode("dark")}
      >
        <Moon className="h-4 w-4" aria-hidden="true" />
      </ToggleButton>
    </div>
  );
}

interface ToggleButtonProps {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}

function ToggleButton({ active, label, onClick, children }: ToggleButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-sm p-1.5 text-muted-foreground transition-colors",
        "hover:text-foreground hover:bg-secondary/60",
        active && "bg-secondary text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// keep an unused-import lint clean
void ({} as ThemeMode);
